import { ISelectable, ISelectionContext, SelectionProps } from 'providers/SelectionProvider/Types'
import { createContext, Key, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Selection } from '@react-types/shared/src/selection'

const SelectionContext = createContext<ISelectionContext>({
  selection: new Set(),
  hasSelection: false,
  toggle: () => {},
  isSelected: (selectable: Key) => false,
  toggleSelectAll: () => {},
  items: [],
  clearSelection: () => {},
  enabled: true,
})

export const SelectionProvider = <Item extends ISelectable = ISelectable>({
  children,
  items,
  initialSelection = [],
  enabled = true,
}: PropsWithChildren<SelectionProps<Item>>) => {
  const [selection, setSelection] = useState<Selection>(() => {
    if (initialSelection === 'all') return 'all'
    return new Set(initialSelection)
  })

  const keys = useMemo(() => items.map((item) => item.id), [items])

  // remove keys from selection if they are no longer present in items
  useEffect(() => {
    setSelection((prev) => {
      if (prev === 'all') return 'all'

      const next = new Set(prev)
      next.forEach((key) => {
        if (!keys.includes(key)) {
          next.delete(key)
        }
      })

      return next
    })
  }, [keys])

  const toggle = useCallback(
    (key: Key) => {
      setSelection((prev) => {
        if (prev === 'all') {
          return new Set(items.filter((item) => item.id !== key).map((item) => item.id))
        }

        const next = new Set(prev)
        if (!next.has(key)) {
          next.add(key)
        } else {
          next.delete(key)
        }
        return next
      })
    },
    [items],
  )

  const isSelected = useCallback(
    (key: Key) => {
      if (selection === 'all') return true

      return selection.has(key)
    },
    [selection],
  )

  const toggleSelectAll = useCallback(() => {
    setSelection((prev) => {
      if (prev === 'all') return new Set()
      return 'all'
    })
  }, [])

  const clearSelection = useCallback(() => {
    setSelection(new Set())
  }, [])

  const hasSelection = selection === 'all' || selection.size > 0

  return (
    <SelectionContext.Provider
      value={{ selection, toggle, isSelected, hasSelection, toggleSelectAll, items, clearSelection, enabled }}
    >
      {children}
    </SelectionContext.Provider>
  )
}

export const useSelection = <Item extends ISelectable = ISelectable>() => useContext(SelectionContext) as ISelectionContext<Item>;
